import React, { useEffect, useState } from "react";
import formatAsCurrency from "@wss/utilities/formatAsCurrency";
import Modal, { Body, Footer, Header } from "~/common/ModalReact";
import { InstallationPricing } from "./getInstallationServicePricingData";
import {
  InstallationServiceSelections,
  InstallationServiceType,
  ServiceLevelType,
} from ".";
import SurveyQuestion from "./SurveyQuestion";

interface Props {
  serviceLevelId: number;
  isModalOpen: boolean;
  formSelections: InstallationServiceSelections;
  setFormSelections: React.Dispatch<
    React.SetStateAction<InstallationServiceSelections>
  >;
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  quantity: number;
  pricingData: InstallationPricing;
  calculateServiceTotalPrice: (
    selections: InstallationServiceSelections,
  ) => number;
}

const InstallationServiceModal = ({
  serviceLevelId,
  isModalOpen,
  setIsModalOpen,
  pricingData,
  calculateServiceTotalPrice,
  quantity,
  formSelections,
  setFormSelections,
}: Props) => {
  const [serviceType, setServiceType] = useState(
    ([ServiceLevelType.Both, ServiceLevelType.FullService].includes(
      serviceLevelId,
    ) &&
      InstallationServiceType.FullService) ||
      (serviceLevelId === ServiceLevelType.FinalConnections &&
        InstallationServiceType.FinalConnections) ||
      InstallationServiceType.FullService,
  );
  const [includeHaulAway, setIncludeHaulAway] = useState(true);
  const [requireGoingToDifferentFloor, setRequireGoingToDifferentFloor] =
    useState(false);
  const [hasFreightElevator, setHasFreightElevator] = useState(true);
  const [stairFlightsQty, setStairFlightsQty] = useState(1);
  const [includeOnSiteSurvey, setIncludeOnSiteSurvey] = useState(true);

  useEffect(() => {
    if (formSelections.serviceType) {
      setCurrentSelectionsFromPrevious();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formSelections.serviceType]);

  const setCurrentSelectionsToDefaults = (type: InstallationServiceType) => {
    setServiceType(type);
    setIncludeHaulAway(true);
    setRequireGoingToDifferentFloor(false);
    setHasFreightElevator(true);
    setStairFlightsQty(1);
    setIncludeOnSiteSurvey(true);
  };

  const getCurrentSelections = () => {
    return {
      includeHaulAway,
      includeOnSiteSurvey,
      hasFreightElevator,
      requireGoingToDifferentFloor,
      serviceType,
      stairFlightsQty,
    };
  };

  const setCurrentSelectionsFromPrevious = () => {
    setServiceType(
      formSelections.serviceType || InstallationServiceType.FullService,
    );
    setIncludeHaulAway(formSelections.includeHaulAway);
    setRequireGoingToDifferentFloor(
      formSelections.requireGoingToDifferentFloor,
    );
    setHasFreightElevator(formSelections.hasFreightElevator);
    setStairFlightsQty(formSelections.stairFlightsQty);
    setIncludeOnSiteSurvey(formSelections.includeOnSiteSurvey);
  };

  // When service is updated
  //    1) If service was not added, reset the states to defaults
  //    2a) If service was added and the new service is not the same as the service added, reset the states to defaults
  //    2b) If service was added and the new service is the same as the service added, set the states to the selections when service was added
  const handleServiceChange = (newService: InstallationServiceType) => {
    if (
      formSelections.serviceType &&
      formSelections.serviceType === newService
    ) {
      setCurrentSelectionsFromPrevious();
    } else {
      setCurrentSelectionsToDefaults(newService);
    }
  };

  // Before closing the modal,
  //    1) If service was not added, reset the states to defaults
  //    2) If service was added, set the states to the selections when service was added
  const handleClose = () => {
    if (formSelections.serviceType) {
      setCurrentSelectionsFromPrevious();
    } else {
      setCurrentSelectionsToDefaults(InstallationServiceType.FullService);
    }
    if (document.body.style.overflow) {
      document.body.removeAttribute("style");
    }
    setIsModalOpen(false);
  };

  const handleAddService = () => {
    setFormSelections(getCurrentSelections());
    if (document.body.style.overflow) {
      document.body.removeAttribute("style");
    }
    setIsModalOpen(false);
  };

  const displayPrice = (showPrice: boolean, price: number) => {
    return formatAsCurrency.formatIfUndefined(showPrice ? price : 0);
  };

  return (
    <Modal
      isOpen={isModalOpen}
      onRequestClose={() => {
        handleClose();
      }}
      onAfterOpen={() => {
        document.body.style.overflow = "hidden";
      }}
      className="max-h-screen md:max-w-2xl"
    >
      <Header
        closeModal={() => handleClose()}
        className="py-4 pl-8 bg-gray-100"
      >
        <div className="p-0 border-t-0 border-r-0 border-l-0 bg-gray-100">
          <h3 className="mb-0 text-lg leading-none">
            Add Installation Services
          </h3>
          <p className="mt-1 mr-2 mb-0 text-sm leading-tight text-gray-700">
            Complete the form below for fast & easy item installation
            coordinated by WebstaurantStore’s trusted service partner, Ready
            Kitchen Warranty!
          </p>
        </div>
      </Header>
      <Body>
        <div className="md:p-2 p-1">
          <fieldset className="border-none m-0 p-0">
            <h3 className="mb-0 text-lg leading-none">
              1. Select service type
            </h3>
            <p className="mt-2 mb-4 text-sm leading-tight text-gray-800">
              Only available for new or replacement units at existing
              facilities.
            </p>
            <div className="grid justify-between gap-3 grid-cols-2">
              {[ServiceLevelType.Both, ServiceLevelType.FullService].includes(
                serviceLevelId,
              ) && (
                <div
                  data-testid="full-service-container"
                  className="h-full border-box flex-grow"
                >
                  <label
                    className={`sm:p-4 p-3 mb-0 shadow rounded border-2 border-solid hover:border-green-400 cursor-pointer h-full ${
                      serviceType === InstallationServiceType.FullService
                        ? "border-green-400 bg-green-50"
                        : "border-gray-200"
                    }`}
                    htmlFor="full-service-radio"
                  >
                    <input
                      type="radio"
                      id="full-service-radio"
                      name="serviceTypeRadio"
                      className="hidden"
                      defaultChecked={
                        serviceType === InstallationServiceType.FullService
                      }
                      onClick={() => {
                        handleServiceChange(
                          InstallationServiceType.FullService,
                        );
                      }}
                    />
                    <div className="mb-2 flex sm:justify-between sm:flex-row flex-col">
                      <div className="inline-block sm:mb-0 mb-1 text-base leading-none">
                        <span id="servicename-fullservice">Full Service</span>
                      </div>
                      <div className="inline-block mb-0 text-base leading-none">
                        {displayPrice(true, pricingData.fullService.price)}
                      </div>
                    </div>
                    <p className="mb-0 text-xs leading-tight text-gray-700 font-normal">
                      Item is delivered to the service partner, who brings it to
                      your location and completes installation.
                    </p>
                  </label>
                </div>
              )}
              {[
                ServiceLevelType.Both,
                ServiceLevelType.FinalConnections,
              ].includes(serviceLevelId) && (
                <div
                  data-testid="final-connections-container"
                  className="h-full border-box flex-grow"
                >
                  <label
                    className={`sm:p-4 p-3 mb-0 shadow rounded border-2 border-solid hover:border-green-400 cursor-pointer h-full ${
                      serviceType === "finalConnections"
                        ? "border-green-400 bg-green-50"
                        : "border-gray-200"
                    }`}
                    htmlFor="final-connections-radio"
                  >
                    <input
                      type="radio"
                      id="final-connections-radio"
                      name="serviceTypeRadio"
                      className="hidden"
                      defaultChecked={
                        serviceType === InstallationServiceType.FinalConnections
                      }
                      onClick={() => {
                        handleServiceChange(
                          InstallationServiceType.FinalConnections,
                        );
                      }}
                    />
                    <div className="mb-2 installation-network-service-type-name flex sm:justify-between sm:flex-row flex-col">
                      <div className="inline-block sm:mb-0 mb-1 text-base leading-none">
                        <span id="servicename-finalconnections">
                          Final Connections
                        </span>
                      </div>
                      <div className="inline-block mb-0 text-base leading-none">
                        {displayPrice(true, pricingData.finalConnections.price)}
                      </div>
                    </div>
                    <p className="mb-0 text-xs leading-tight text-gray-700 font-normal">
                      Item is delivered to your location to ensure it&apos;s
                      ready; our service partner completes installation.
                    </p>
                  </label>
                </div>
              )}
            </div>
          </fieldset>
          <fieldset className="border-none m-0 p-0">
            <h3 className="mt-4 mb-2 text-lg leading-none">
              2. Fill out Pre-Install Survey
            </h3>
            <SurveyQuestion
              currentValue={includeHaulAway}
              name="includeHaulAwayRadio"
              price={displayPrice(includeHaulAway, pricingData.haulAway.price)}
              question="Will you require disposal of the old unit?"
              setState={setIncludeHaulAway}
              yesText="Haul Away"
              noText="No"
            />
            {serviceType === InstallationServiceType.FullService && (
              <>
                <SurveyQuestion
                  currentValue={requireGoingToDifferentFloor}
                  name="requireGoingToDifferentFloorRadio"
                  question="Will delivery require going to a different floor other than the ground floor?"
                  setState={setRequireGoingToDifferentFloor}
                  yesText="Yes"
                  noText="No"
                />
                {requireGoingToDifferentFloor && (
                  <SurveyQuestion
                    currentValue={hasFreightElevator}
                    name="hasFreightElevatorRadio"
                    question=" Is a freight elevator available to take the equipment to this floor?"
                    setState={setHasFreightElevator}
                    yesText="Yes"
                    noText="No"
                  />
                )}
                {requireGoingToDifferentFloor && !hasFreightElevator && (
                  <fieldset
                    className="border-none m-0 p-0"
                    data-testid="stairFlightsQty"
                  >
                    <div className="mb-3 leading-none">
                      <label className="inline-block w-9/12 mb-0 text-sm font-normal leading-none">
                        How many flights of stairs will the install provider
                        need to take to get the item to the correct floor?
                        <input
                          className="block mt-2"
                          type="number"
                          min="1"
                          max="99"
                          name="stairFlightsQty"
                          data-testid="stairFlightsQty-input"
                          onChange={(event) =>
                            setStairFlightsQty(Number(event.target.value))
                          }
                          value={stairFlightsQty}
                        />
                      </label>
                      <div className="inline-block float-right mb-0 text-base leading-none">
                        {displayPrice(
                          true,
                          stairFlightsQty > 3
                            ? pricingData.fourToNinetyNineFlights.price
                            : pricingData.oneToThreeFlights.price,
                        )}
                      </div>
                    </div>
                  </fieldset>
                )}
                <SurveyQuestion
                  currentValue={includeOnSiteSurvey}
                  name="requiresSurveyRadio"
                  price={displayPrice(
                    includeOnSiteSurvey,
                    pricingData.surveyNoCharge.price,
                  )}
                  question="Include a professional on-site survey prior to installation?"
                  moreInfo="Included free with Full Service Installation!"
                  setState={setIncludeOnSiteSurvey}
                  yesText="Yes"
                  noText="No, I'll supply required information & photos"
                  disableNoOption
                />
              </>
            )}
            {serviceType === InstallationServiceType.FinalConnections && (
              <SurveyQuestion
                currentValue={includeOnSiteSurvey}
                name="requiresSurveyRadio"
                price={displayPrice(
                  includeOnSiteSurvey,
                  pricingData.survey.price,
                )}
                question="Include a professional on-site survey prior to installation?"
                moreInfo="If selected, WebstaurantStore will cover extra fees that occur the day of installation that result in the installation not being successful!"
                setState={setIncludeOnSiteSurvey}
                yesText="Yes"
                noText="No, I'll supply required information & photos"
              />
            )}
          </fieldset>
          <div
            className="pt-5 mb-4 leading-none border-t border-r-0 border-b-0 border-l-0 border-solid border-gray-200"
            data-testid="service-total-container"
          >
            <div className="inline-block w-9/12 mb-0 text-lg font-bold leading-none">
              {"Service Total* "}
              <span className="text-gray-700">{`(Qty: ${quantity})`}</span>
            </div>
            <div className="inline-block float-right mb-0 text-lg text-red-700 font-bold leading-none">
              <span id="serviceTotalPrice">
                {formatAsCurrency.formatIfUndefined(
                  calculateServiceTotalPrice(getCurrentSelections()),
                )}
              </span>
            </div>
          </div>
          <p className="mb-0 text-xs leading-tight text-gray-400 font-normal">
            *The service price is in addition to the price of the item.
            Installation fees are subject to change based on information given
            here and in the post-order survey. Ready Kitchen Warranty will
            contact you, on behalf of WebstaurantStore, once your order has been
            placed to finalize the installation service price. For full terms,
            please see WebstaurantStore&apos;s&nbsp;
            <a
              className="white-space-no-wrap"
              href="/policies.html"
              target="_blank"
            >
              Conditions of Use
            </a>
            .
          </p>
        </div>
      </Body>
      <Footer>
        <button
          name="close"
          type="button"
          className="btn btn-light-alt inline-block w-auto pl-8 pr-8 mr-2"
          aria-label="Close"
          onClick={() => {
            handleClose();
          }}
        >
          Cancel
        </button>
        <button
          type="button"
          className="inline-block border-none w-auto p-0"
          onClick={handleAddService}
        >
          <label className="btn btn-primary">Add Service</label>
        </button>
      </Footer>
    </Modal>
  );
};

export default InstallationServiceModal;
